/**
 * Context-based "hoist into AppShell header" mechanism.
 *
 * AppShell exposes dos slots vacíos (title + actions). El componente
 * <PageHeader/> de cada ruta usa Portales para inyectar su contenido en
 * esos slots, en lugar de renderearse inline. Eso libera el espacio
 * vertical que ocupaba el header por-ruta y aprovecha el espacio muerto
 * del topbar global.
 *
 * Caída si no hay <PageHeader/> en la ruta activa: AppShell muestra el
 * label del item de navegación (`activeItem.label`) como fallback. Un
 * contador `pageHeaderCount` rastrea cuántos PageHeader están montados;
 * los efectos de mount/unmount se batchan en una sola actualización
 * gracias al auto-batching de React 18+, así que no hay flicker al
 * navegar entre rutas.
 */
import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

interface PageTitleSlots {
  titleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  setTitleEl: (el: HTMLElement | null) => void;
  setActionsEl: (el: HTMLElement | null) => void;
  pageHeaderCount: number;
  register: () => void;
  unregister: () => void;
}

const Ctx = createContext<PageTitleSlots>({
  titleEl: null,
  actionsEl: null,
  setTitleEl: () => {},
  setActionsEl: () => {},
  pageHeaderCount: 0,
  register: () => {},
  unregister: () => {},
});

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [titleEl, setTitleEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const [pageHeaderCount, setPageHeaderCount] = useState(0);

  return (
    <Ctx.Provider
      value={{
        titleEl,
        actionsEl,
        setTitleEl,
        setActionsEl,
        pageHeaderCount,
        register: () => setPageHeaderCount((c) => c + 1),
        unregister: () => setPageHeaderCount((c) => c - 1),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePageTitleSlots() {
  return useContext(Ctx);
}

/**
 * Marca un PageHeader como "presente" durante su ciclo de vida.
 * AppShell usa el contador para decidir si renderear el fallback.
 */
export function usePageHeaderRegistration() {
  const { register, unregister } = useContext(Ctx);
  useEffect(() => {
    register();
    return () => unregister();
    // register/unregister son estables (vienen del provider state); no
    // queremos re-registrar al re-renderear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
